import React, { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, List, ListItem, Stack } from '@mui/material'
import SearchOffSharpIcon from '@mui/icons-material/SearchOffSharp'
import { IVehicleSearchFormatResults } from 'core/vehicles/mappings/vehicle'
import { ISearchResponse, ResultObjectType } from '@cbgms/api'
import { useArrowScrollHandler } from '@carsys/ui/src/helpers/keyboard/use-arrow-scroll-handler'
import { formatSearchErrors, formatSearchResults } from 'core/vehicles/mappings/vehicleSearchFormat'
import { useFilterSilentSearchErrors } from 'app/search/hooks/useFilterSilentSearchErrors'
import { LoadingSkeleton } from '../SearchResultLoadingSekeleton'
import { usePrimaryActionBuilder } from 'app/menu/components/Search/hooks/use-primary-action-builder'
import { NoResults } from '@cbgms/components/src/utils/NoResults'
import { ISearchResultWithTitle, ResultComponentSelector } from '@cbgms/components/src/top-search/search-v1/Results/ResultComponentSelector'
import { SearchResultTypeContextProvider } from 'app/menu/components/Search/SearchResultTypeContextProvider'

interface SearchResultsProps {
  query: string
  clearQuery: () => void
  searchResponse: ISearchResponse | undefined
  loading: boolean
  ResultItem?: React.FC<ResultItemProps>
  NoSearchResult?: React.FC<NoResultProps>
  noResultCallBack?: () => void
}

export interface ResultItemProps extends IVehicleSearchFormatResults {
  focus: boolean
  query: string
  clearQuery: () => void
}

interface NoResultProps {
  clearQuery: () => void
  handleClick: () => void
}

export interface ISearchResultDialogProps extends ISearchResultWithTitle {
  Type: ResultObjectType
  Score: number
  query: string
  focus?: boolean
  clearQuery: () => void
  primaryAction: () => Promise<void>
  hasGroupHeader: boolean
}

const useSearchDialogResultsMapper = (
  query: string,
  searchResponse: ISearchResponse | undefined,
  clearQuery: () => void
): { searchPageResults: ISearchResultDialogProps[] } => {
  const formatedSearchResults = formatSearchResults(searchResponse)
  const formatedSearchErrors = formatSearchErrors(searchResponse, useFilterSilentSearchErrors)
  const actionBuilder = usePrimaryActionBuilder({ clearQuery: () => {} })
  return useMemo(() => {
    const searchPageResults = [...formatedSearchResults, ...formatedSearchErrors].map(v => ({
      ...v,
      query,
      clearQuery,
      primaryAction: actionBuilder(v.Type, query, v),
      hasGroupHeader: true
    }))
    return { searchPageResults }
  }, [formatedSearchResults, actionBuilder, query, clearQuery])
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  query,
  clearQuery,
  searchResponse,
  loading,
  noResultCallBack,
  ResultItem = ResultComponentSelector,
  NoSearchResult = NoVehicleSearchResults
}) => {
  const { push } = useHistory()
  const { currentFocus } = useArrowScrollHandler(searchResponse?.Results?.length || 0)
  const { searchPageResults } = useSearchDialogResultsMapper(query, searchResponse, clearQuery)

  const defaultNoResultClick = () => {
    push('/vehicles/quick-create/')
  }

  if (loading) {
    return <LoadingSkeleton />
  }

  if (!loading && query.length > 0 && !searchPageResults?.length) {
    return <NoSearchResult clearQuery={clearQuery} handleClick={noResultCallBack || defaultNoResultClick} />
  }

  return (
    <SearchResultTypeContextProvider>
      <List dense disablePadding>
        {searchPageResults.map((result, index) => {
          const resultCount = searchResponse?.TotalResultCount || 0
          const focus = currentFocus === index || resultCount === 1
          return (
            result.Vehicle && (
              <ListItem
                key={index}
                divider={index + 1 !== resultCount}
                sx={{ ':hover': { backgroundColor: theme => theme.palette.action.focus }, p: 1 }}
                className={focus ? 'Mui-focusVisible' : ''}
              >
                <ResultItem focus={focus} {...result} />
              </ListItem>
            )
          )
        })}
      </List>
    </SearchResultTypeContextProvider>
  )
}

export function NoVehicleSearchResults({ handleClick }: Omit<NoResultProps, 'clearQuery'>) {
  const { t } = useTranslation()
  return (
    <Stack direction='column' spacing={2}>
      <Button sx={{ height: '100%' }} color='primary' variant='outlined' fullWidth onClick={handleClick}>
        {t('vehicleLookup_widget_select_via_tree')}
      </Button>
      <NoResults>{t('vehicleLookup_widget_noResult')}</NoResults>
      <SearchOffSharpIcon sx={{ fontSize: '6rem', alignSelf: 'center', color: theme => theme.palette.action.disabled }} />
    </Stack>
  )
}
